import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Select } from '@/shared/components/Select';
import { Textarea } from '@/shared/components/Textarea';
import { Input } from '@/shared/components/Input';
import { Button } from '@/shared/components/Button';
import { ApiError } from '@/lib/api-client';
import { formatDate } from '@/shared/utils/format';
import { useCreateRequest } from '../hooks/useMyRequests';
import { useMyClasses } from '../hooks/useMyClasses';
import { useClassChangeDestinations } from '../hooks/useClassChangeDestinations';
import { REQUEST_TYPE_LABELS } from '../utils/labels';
const TYPE_OPTIONS = Object.entries(REQUEST_TYPE_LABELS).map(([value, label]) => ({
    value,
    label,
}));
function classLabel(c) {
    return `${formatDate(c.date)} · ${c.subject_name} (${c.start_time.slice(0, 5)}–${c.end_time.slice(0, 5)})`;
}
export function NewRequestForm({ onSuccess }) {
    const [serverError, setServerError] = useState(null);
    const createRequest = useCreateRequest();
    const { data: classes = [] } = useMyClasses();
    const { register, handleSubmit, watch, setValue, formState: { errors }, } = useForm({ defaultValues: { type: '' } });
    const type = watch('type');
    const fromClassId = watch('from_class_id');
    // Destinations come from the backend: the SAME subject in ANOTHER shift (the
    // student's own schedule never contains other shifts' classes). You can only
    // swap a class that hasn't happened yet, so the origin list is future-only.
    const today = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Madrid' }).format(new Date());
    const fromClassOptions = classes.filter((c) => c.date >= today);
    const { data: destinations = [], isFetching: loadingDestinations } = useClassChangeDestinations(fromClassId);
    // Reset the chosen destination whenever the origin changes (the previous pick
    // belongs to a different subject/shift list).
    useEffect(() => {
        setValue('to_class_id', '');
    }, [fromClassId, setValue]);
    // Classes the student can request material for: those with assigned materials.
    const materialClassOptions = classes.filter((c) => c.material_count > 0);
    function onSubmit(values) {
        setServerError(null);
        let metadata = {};
        if (values.type === 'shift_change') {
            metadata = {
                desired_shift_id: values.desired_shift_id,
                ...(values.shift_reason && { reason: values.shift_reason }),
            };
        }
        else if (values.type === 'single_class_change') {
            metadata = {
                from_class_id: values.from_class_id,
                to_class_id: values.to_class_id,
            };
        }
        else if (values.type === 'class_material_access') {
            metadata = {
                schedule_class_id: values.schedule_class_id,
            };
        }
        else if (values.type === 'temporary_leave') {
            metadata = {
                reason: values.leave_reason ?? '',
                ...(values.expected_return_date && { expected_return_date: values.expected_return_date }),
            };
        }
        else if (values.type === 'data_correction') {
            metadata = {
                field_to_correct: values.field_to_correct ?? '',
                current_value: values.current_value ?? '',
                desired_value: values.desired_value ?? '',
            };
        }
        createRequest.mutate({ type: values.type, description: values.description, metadata }, {
            onSuccess: () => onSuccess(),
            onError: (err) => {
                setServerError(err instanceof ApiError
                    ? err.message
                    : 'Ha ocurrido un error. Inténtalo de nuevo.');
            },
        });
    }
    return (_jsxs("form", { onSubmit: handleSubmit(onSubmit), className: "flex flex-col gap-5", noValidate: true, children: [_jsx(Select, { label: "Tipo de solicitud", options: TYPE_OPTIONS, placeholder: "Selecciona un tipo", error: errors.type?.message, ...register('type', { required: 'Selecciona el tipo de solicitud' }) }), _jsx(Textarea, { label: "Descripci\u00F3n", hint: "M\u00EDnimo 10 caracteres, m\u00E1ximo 2000", error: errors.description?.message, ...register('description', {
                    required: 'La descripción es obligatoria',
                    minLength: { value: 10, message: 'Mínimo 10 caracteres' },
                    maxLength: { value: 2000, message: 'Máximo 2000 caracteres' },
                }) }), type === 'shift_change' && (_jsxs(_Fragment, { children: [_jsx(Input, { label: "ID del turno deseado", hint: "UUID del turno al que deseas cambiarte", error: errors.desired_shift_id?.message, ...register('desired_shift_id', {
                            required: 'Introduce el ID del turno deseado',
                        }) }), _jsx(Input, { label: "Motivo (opcional)", error: errors.shift_reason?.message, ...register('shift_reason') })] })), type === 'single_class_change' && (_jsxs(_Fragment, { children: [_jsx(Select, { label: "Clase a cambiar", options: fromClassOptions.map((c) => ({ value: c.id, label: classLabel(c) })), placeholder: "Selecciona la clase", error: errors.from_class_id?.message, ...register('from_class_id', { required: 'Selecciona la clase a cambiar' }) }), _jsx(Select, { label: "Clase de destino", options: destinations.map((d) => ({
                            value: d.id,
                            label: `${formatDate(d.date)} · ${d.subject_name} · ${d.shift_name} (${d.start_time.slice(0, 5)}–${d.end_time.slice(0, 5)})${d.is_full ? ' · completo' : ''}`,
                            disabled: d.is_full,
                        })), placeholder: !fromClassId
                            ? 'Selecciona primero la clase a cambiar'
                            : loadingDestinations
                                ? 'Cargando…'
                                : destinations.length
                                    ? 'Selecciona el destino'
                                    : 'No hay clases de esa asignatura en otros turnos', disabled: !fromClassId, error: errors.to_class_id?.message, ...register('to_class_id', { required: 'Selecciona la clase de destino' }) }), _jsx("p", { className: "text-xs text-gray-500 dark:text-gray-400", children: "Se muestran clases de la misma asignatura impartidas en otros turnos. El director validar\u00E1 el balance de clases." })] })), type === 'class_material_access' && (_jsx(Select, { label: "Clase", options: materialClassOptions.map((c) => ({ value: c.id, label: classLabel(c) })), placeholder: materialClassOptions.length ? 'Selecciona la clase' : 'No tienes clases con material asignado', error: errors.schedule_class_id?.message, ...register('schedule_class_id', { required: 'Selecciona la clase' }) })), type === 'temporary_leave' && (_jsxs(_Fragment, { children: [_jsx(Input, { label: "Motivo de la baja", error: errors.leave_reason?.message, ...register('leave_reason', { required: 'El motivo es obligatorio' }) }), _jsx(Input, { label: "Fecha de regreso prevista (opcional)", type: "date", error: errors.expected_return_date?.message, ...register('expected_return_date') })] })), type === 'data_correction' && (_jsxs(_Fragment, { children: [_jsx(Input, { label: "Campo a corregir", error: errors.field_to_correct?.message, ...register('field_to_correct', { required: 'Indica qué campo corregir' }) }), _jsx(Input, { label: "Valor actual", error: errors.current_value?.message, ...register('current_value', { required: 'Indica el valor actual' }) }), _jsx(Input, { label: "Valor deseado", error: errors.desired_value?.message, ...register('desired_value', { required: 'Indica el valor deseado' }) })] })), serverError && (_jsx("p", { role: "alert", className: "text-sm text-red-600 dark:text-red-400", children: serverError })), _jsx(Button, { type: "submit", loading: createRequest.isPending, className: "w-full", children: "Enviar solicitud" })] }));
}
//# sourceMappingURL=NewRequestForm.js.map